import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Cloud, Eye, EyeOff, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { connectPMS, testPMSConnection } from '../../services/pmsApi';

const PMSConfigModal = ({ isOpen, onClose, hotel, onSuccess }) => {
  const [selectedProvider, setSelectedProvider] = useState('cloudbeds');
  const [credentials, setCredentials] = useState({
    apiKey: '',
    propertyId: '',
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null); // { success: true/false, message: '', propertyName: '' }

  const providers = [
    { id: 'cloudbeds', name: 'Cloudbeds', available: true },
    { id: 'opera', name: 'Opera', available: false },
    { id: 'mews', name: 'Mews', available: false },
  ];

  const handleProviderSelect = (providerId) => {
    setSelectedProvider(providerId);
    setTestResult(null);
    setCredentials({ apiKey: '', propertyId: '' });
  };

  const handleTestConnection = async () => {
    if (!credentials.apiKey.trim() || !credentials.propertyId.trim()) {
      setTestResult({
        success: false,
        message: 'Please enter both API Key and Property ID',
      });
      return;
    }

    if (!hotel?.brand_id || !hotel?.id) {
      setTestResult({
        success: false,
        message: 'Hotel information is missing',
      });
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const response = await testPMSConnection(hotel.brand_id, hotel.id, {
        provider: selectedProvider,
        apiKey: credentials.apiKey,
        propertyId: credentials.propertyId,
      });

      setTestResult({
        success: true,
        message: response.message || 'Connection successful!',
        propertyName: response.property_name || hotel.name,
      });
    } catch (error) {
      setTestResult({
        success: false,
        message: error.message || 'Failed to connect. Please check your credentials.',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSaveAndConnect = async () => {
    if (!testResult?.success) {
      setTestResult({
        success: false,
        message: 'Please test the connection first',
      });
      return;
    }

    if (!hotel?.brand_id || !hotel?.id) {
      setTestResult({
        success: false,
        message: 'Hotel information is missing',
      });
      return;
    }

    setSaving(true);

    try {
      const response = await connectPMS(hotel.brand_id, hotel.id, {
        provider: selectedProvider,
        apiKey: credentials.apiKey,
        propertyId: credentials.propertyId,
      });

      // Call success callback
      if (onSuccess) {
        onSuccess({
          provider: selectedProvider,
          propertyId: credentials.propertyId,
          response: response,
        });
      }

      // Close modal
      onClose();
    } catch (error) {
      setTestResult({
        success: false,
        message: error.message || 'Failed to save connection',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setCredentials({ apiKey: '', propertyId: '' });
    setTestResult(null);
    setShowApiKey(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Cloud className="h-5 w-5" />
            <span>PMS Configuration - {hotel?.name}</span>
          </DialogTitle>
          <DialogDescription>
            Connect your Property Management System to sync rates, reservations, and room types
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Provider Selection */}
          <div className="space-y-3">
            <Label>Select PMS Provider</Label>
            <div className="grid grid-cols-3 gap-3">
              {providers.map((provider) => (
                <button
                  key={provider.id}
                  onClick={() => provider.available && handleProviderSelect(provider.id)}
                  disabled={!provider.available}
                  className={`
                    relative p-4 border-2 rounded-lg text-center transition-all
                    ${selectedProvider === provider.id 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                    }
                    ${!provider.available ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                >
                  <div className="font-medium">{provider.name}</div>
                  {!provider.available && (
                    <Badge variant="secondary" className="absolute top-2 right-2 text-xs">
                      Soon
                    </Badge>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Cloudbeds Configuration */}
          {selectedProvider === 'cloudbeds' && (
            <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
              <div className="flex items-center space-x-2 mb-2">
                <Cloud className="h-4 w-4 text-blue-600" />
                <h3 className="font-semibold">Cloudbeds Configuration</h3>
              </div>

              {/* API Key */}
              <div className="space-y-2">
                <Label htmlFor="api-key">API Key *</Label>
                <div className="flex space-x-2">
                  <Input
                    id="api-key"
                    type={showApiKey ? 'text' : 'password'}
                    placeholder="Enter your Cloudbeds API key"
                    value={credentials.apiKey}
                    onChange={(e) => setCredentials({ ...credentials, apiKey: e.target.value })}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="px-3"
                  >
                    {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Find your API key in Cloudbeds: Settings → Integrations → API
                </p>
              </div>

              {/* Property ID */}
              <div className="space-y-2">
                <Label htmlFor="property-id">Property ID *</Label>
                <Input
                  id="property-id"
                  type="text"
                  placeholder="e.g., 116386416918720"
                  value={credentials.propertyId}
                  onChange={(e) => setCredentials({ ...credentials, propertyId: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Your property ID can be found in your Cloudbeds account settings
                </p>
              </div>

              {/* Test Connection Button */}
              <Button
                onClick={handleTestConnection}
                disabled={testing || !credentials.apiKey.trim() || !credentials.propertyId.trim()}
                variant="outline"
                className="w-full"
              >
                {testing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Testing Connection...
                  </>
                ) : (
                  'Test Connection'
                )}
              </Button>

              {/* Test Result */}
              {testResult && (
                <div
                  className={`
                    flex items-start space-x-2 p-3 rounded-md
                    ${testResult.success 
                      ? 'bg-green-50 border border-green-200' 
                      : 'bg-red-50 border border-red-200'
                    }
                  `}
                >
                  {testResult.success ? (
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p
                      className={`text-sm font-medium ${
                        testResult.success ? 'text-green-900' : 'text-red-900'
                      }`}
                    >
                      {testResult.message}
                    </p>
                    {testResult.success && testResult.propertyName && (
                      <p className="text-xs text-green-700 mt-1">
                        Property: {testResult.propertyName}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Sync Settings */}
          {testResult?.success && (
            <div className="space-y-3 p-4 border rounded-lg">
              <h3 className="font-semibold text-sm">Sync Settings</h3>
              <div className="space-y-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    defaultChecked
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">Auto-sync every 6 hours</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    defaultChecked
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">Sync on webhook events (real-time updates)</span>
                </label>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveAndConnect}
            disabled={!testResult?.success || saving}
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Connecting...
              </>
            ) : (
              'Save & Connect'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PMSConfigModal;
